import { createContext, useContext, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { onAnimationFrame } from './animation_loop.ts';

/**
 * How many times larger than its layout a canvas is shown, where a CSS transform scales it up (a
 * style's preview, tile-styles.md TY-5): its backing store has that many more pixels, so that it
 * stays sharp. 1 where nothing scales it up.
 */
export const CanvasMagnificationContext = createContext(1);

/** Draws on a canvas in CSS pixels; `now` is the frame time (`performance.now()` clock). */
export type Draw = (ctx: CanvasRenderingContext2D, width: number, height: number, now: number) => void;

export interface CanvasViewProps {
    draw: Draw;
    /** Draw again when one of these changes (besides the size and the pixel ratio). */
    deps?: readonly unknown[];
    /** Draw on every frame of the shared animation loop. */
    animate?: boolean;
    style?: CSSProperties;
}

/**
 * A canvas that fills its parent and is sharp on high-density screens: the backing store has
 * device pixels, the drawing code works in CSS pixels. It draws again when its size changes and
 * once the web fonts have loaded.
 */
export function CanvasView({ draw, deps = [], animate = false, style }: CanvasViewProps) : ReactNode {
    const magnification = useContext(CanvasMagnificationContext);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawRef = useRef(draw);
    drawRef.current = draw;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        let frame = 0;
        let isMounted = true;
        // Its size in the layout, before any CSS transform: a tile drawn scaled down (a style's
        // preview, tile-styles.md TY-5) is laid out at its own size. The observer gives it with
        // fractions, which `clientWidth` rounds.
        let size = { width: canvas.clientWidth, height: canvas.clientHeight };

        const paint = (now: number = performance.now()) : void => {
            frame = 0;
            const { width, height } = size;
            const ratio = (window.devicePixelRatio || 1) * magnification;
            const pixelWidth = Math.max(1, Math.round(width * ratio));
            const pixelHeight = Math.max(1, Math.round(height * ratio));
            if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
                canvas.width = pixelWidth;
                canvas.height = pixelHeight;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }

            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            ctx.clearRect(0, 0, width, height);
            drawRef.current(ctx, width, height, now);
        };

        const schedule = () : void => {
            if (frame === 0) {
                frame = requestAnimationFrame(paint);
            }
        };

        const repaintWhenFontsLoaded = async () : Promise<void> => {
            await document.fonts.ready;
            if (isMounted) {
                schedule();
            }
        };

        const observer = new ResizeObserver((entries) => {
            const box = entries.at(-1)?.contentRect;
            if (box) {
                size = { width: box.width, height: box.height };
            }
            schedule();
        });
        observer.observe(canvas);

        // A canvas starts the download of a web font only when it first draws with it, and
        // `document.fonts.ready` may already have resolved by then. So draw again whenever a
        // font finishes loading, not only once.
        document.fonts.addEventListener('loadingdone', schedule);
        void repaintWhenFontsLoaded();
        schedule();
        const stopAnimating = animate ? onAnimationFrame(paint) : undefined;

        return () => {
            isMounted = false;
            stopAnimating?.();
            document.fonts.removeEventListener('loadingdone', schedule);
            observer.disconnect();
            if (frame !== 0) {
                cancelAnimationFrame(frame);
            }
        };
        // `draw` is read through a ref; only `deps` and `animate` decide when to draw again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, animate, magnification]);

    return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', ...style }} />;
}
